import React, { useState, useEffect } from "react";
import ModalPage from "components/explore/layout/ModalPage";
import { withStyles } from "@material-ui/core/styles";
import {
  Typography,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio
} from "@material-ui/core";
import { fetchExploreViews, createExploreView, requestExploreSelectedViewIdChange, deleteExploreView, 
  setConfirmationModalState, setExploreSelectedViewIsOpen, appNotify } from "store/actions";
import { connect } from "react-redux";
import { compose } from "recompose";
import { sendClientLog } from 'services/logService';
import { Constants } from 'utilities/constants';

function DuplicateViewModal({ classes, createExploreView, defaultValue,
  modalOpen, viewsLoadingState, onClose, isMobile, isSideBarOpen
}) {
  const [viewNameInput, setViewNameInput] = useState(defaultValue ?? '');
  const [viewNameError, setViewNameError] = useState('');
  const [viewPrivacy, setViewPrivacy] = useState('Private');

  useEffect(() => {
    setDefaultName();
  }, [defaultValue]);

  const setDefaultName = () => {
    setViewNameInput(defaultValue ?? '');
    setViewNameError('');
    setViewPrivacy('Private');
  }

  const handleViewNameInputChange = e => {
    const maxLength = Constants.ViewsCharLimit.VIEWNAME;
    if (e.target.value.length > maxLength && !viewNameError) {
      setViewNameError(`Length must be ${maxLength} characters or less`);
    }
    if (viewNameError?.length && e.target.value.length <= maxLength) {
      setViewNameError('');
    }
    setViewNameInput(e.target.value);
  }

  const handleViewPrivacyChange = e => {
    setViewPrivacy(e.target.value);
  }

  const onCreateNewViewClick = () => {
    if (viewNameInput === '') {
      setViewNameError('View name is required');
      return;
    }
    const newView = { name: viewNameInput, privacy: viewPrivacy, fromExisting: true };
    createExploreView(newView);
    sendClientLog('Info',`Explore Views - User clicked Create on DuplicateViewModal. Name: ${newView.name}, Privacy: ${newView.privacy}`);
    setDefaultName();
    setViewPrivacy('Private');
    onClose();
  }

  const { isLoading } = viewsLoadingState;
  return (
    <ModalPage
      maxWidth="md"
      fullScreen={isMobile && isSideBarOpen}
      title="Duplicate View"
      open={modalOpen}
      onClose={onClose}
    >
      <div className={isMobile ? classes.createNewViewFormMobile : classes.createNewViewForm}>
        <TextField
          autoFocus
          label='View Name'
          placeholder='Enter a name...'
          onChange={handleViewNameInputChange}
          onBlur={() => setViewNameInput(viewNameInput.trim())}
          value={viewNameInput}
          style={{width: isMobile ? 'unset' : 346, height: isMobile? 'unset' : 68, marginBottom: isMobile ? '15px' : 'unset', marginRight: 16}}
          InputLabelProps={{
            shrink: true,
          }}
          error={viewNameError?.length > 0}
          helperText={viewNameError}
        />
        <FormControl component="fieldset" style={{marginRight: 32, marginBottom: 12}}>
          <FormLabel component="legend">Privacy</FormLabel>
          <RadioGroup row aria-label="privacy" name="privacy" value={viewPrivacy} onChange={handleViewPrivacyChange} style={{flexWrap: 'nowrap', marginLeft: isMobile ? '10px' : ''}}>
            <FormControlLabel value="Private" style={{marginRight: isMobile ? '13px' : '', marginTop: isMobile ? '3px' : ''}} control={<Radio size='small' style={{padding: 4}}/>} label="Private" />
            <FormControlLabel value="Public" style={{marginRight: isMobile ? '13px' : '', marginTop: isMobile ? '3px' : ''}} control={<Radio size='small' style={{padding: 4}}/>} label="Public" />
          </RadioGroup>
        </FormControl>
        <div style={{flexGrow: 1, textAlign: 'end'}}>
          <Typography variant='caption'>
            {`The current View, including any pending changes, will be duplicated into a new unsaved View.`}
          </Typography>
        </div>
        <Button
          variant='outlined'
          color='primary'
          onClick={onCreateNewViewClick}
          disabled={isLoading || viewNameError?.length > 0}
          style={{margin: '0 8px'}}
        >
          Create
        </Button>
      </div>
    </ModalPage>
  );
}

const styles = (theme) => ({
  locationToggleContainer: {
    margin: '-44px 0px 1px 4px',
    display: 'flex',
    alignItems: 'center'
  },
  tooltipIcon: {
    zIndex: 1,
    fontSize: 16,
    marginLeft: 4
  },
  footnote: {
    paddingTop: 10,
  },
  spacer: {
    height: 16,
  },
  savedFilterButtons: {
    fontSize: 12,
  },
  btnApply: {
    marginRight: 10,
    padding: 2,
    background: "rgba(0, 0, 0, 0.1)",
    borderRadius: `5px`,
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.3)",
      cursor: `pointer`,
    },
  },
  refreshButton: {
    padding: 2,
    marginLeft: 6
  },
  queryBuilder: {
    '& div.e-rule-field': {
      display: 'flex'
    },
    '& div.e-rule-filter': {
      width: '400px !important'
    },
    '& div.e-rule-value-delete': {
      display: 'flex',
      alignItems: 'center'
    }
  },
  expandButton: {
    transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    padding: 2,
    marginRight: 6
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 426
  },
  createNewViewForm: {
    display: 'flex', alignItems: 'center', border: '1px solid #e0e0e0', padding: '8px 8px 4px'
  },
  createNewViewFormMobile: {
    display: 'flex', flexDirection: 'column', border: '1px solid #e0e0e0', padding: '10px'
  }
});

function mapState(state) {
  const { exploreViews, appState } = state;
  return {
    user: appState.user,
    views: exploreViews.views,
    viewsLoadingState: exploreViews.viewsLoadingState,
    hasSelectedView: exploreViews.selectedViewId != null,
    isSideBarOpen: exploreViews.isSideBarOpen,
    isMobile: appState.isMobile,

  };
}

const mapDispatch = {
  fetchExploreViews,
  createExploreView,
  requestExploreSelectedViewIdChange,
  deleteExploreView,
  setConfirmationModalState,
  setExploreSelectedViewIsOpen,
  appNotify
};

export default compose(withStyles(styles), connect(mapState, mapDispatch))(DuplicateViewModal);
